import { COVER_CROP_MIX_TYPE, DEFAULT_PROJECT_SETTINGS, DEFAULT_SCENARIO_SETTINGS, SCENARIO_LIMITS } from "consts";

import type { SoilSightCropYear, SoilSightScenarioDetail, SoilSightSettingsRefinements, UpdateSettingsRefinementsBody } from "models";

import type { CropYear, ProjectSettings, ScenarioData, ScenarioSettings, SettingsPayloadInput } from "pages/app/scenario-analysis/types/scenario";
import { createEmptyYear } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { toTimelineOption } from "pages/app/scenario-analysis/utils/projectSettings";

type SettingsDefaults = {
  scenario: ScenarioSettings;
  project: Pick<ProjectSettings, "location" | "variableCosts" | "modelSettings">;
};

const DEFAULT_SETTINGS_DEFAULTS: SettingsDefaults = {
  scenario: DEFAULT_SCENARIO_SETTINGS,
  project: {
    location: DEFAULT_PROJECT_SETTINGS.location,
    variableCosts: DEFAULT_PROJECT_SETTINGS.variableCosts,
    modelSettings: DEFAULT_PROJECT_SETTINGS.modelSettings,
  },
};

export const toApiCropYear = (year: CropYear, yearIndex: number): SoilSightCropYear => ({
  yearIndex,
  mainCrop: year.crop || null,
  yield: year.yield ?? null,
  yieldPrice: year.sellingPrice ?? null,
  yieldLossFodder: year.fodderLosses ?? null,
  yieldLossMould: year.mouldLosses ?? null,
  nFertCosts: year.minFertiliser ?? null,
  plantProtectionCosts: year.plantProtection ?? null,
  seedCosts: year.seedCosts ?? null,
  seedAmount: year.seedAmount ?? null,
  tillageId: year.tillageId ?? null,
  residue: year.residue ?? null,
  irrigation: year.irrigation ?? null,
  coverCropId: year.coverCropId ?? null,
  coverCropLegumeShare: year.legumeShare ?? null,
  coverCropCost: year.ccCost ?? null,
});

export const fromApiCropYear = (apiYear: SoilSightCropYear, id: string): CropYear => ({
  id,
  crop: apiYear.mainCrop ?? "",
  coverCropId: apiYear.coverCropId ?? COVER_CROP_MIX_TYPE.NONE,
  tillageId: apiYear.tillageId ?? null,
  residue: apiYear.residue ?? null,
  irrigation: apiYear.irrigation ?? null,
  yield: apiYear.yield ?? null,
  sellingPrice: apiYear.yieldPrice ?? null,
  fodderLosses: apiYear.yieldLossFodder ?? null,
  mouldLosses: apiYear.yieldLossMould ?? null,
  minFertiliser: apiYear.nFertCosts ?? null,
  plantProtection: apiYear.plantProtectionCosts ?? null,
  seedCosts: apiYear.seedCosts ?? null,
  seedAmount: apiYear.seedAmount ?? null,
  legumeShare: apiYear.coverCropLegumeShare ?? null,
  ccCost: apiYear.coverCropCost ?? null,
  // ISO → Unix ms; undefined until the next GET after a save, which ranks lowest when inheriting.
  updatedAt: apiYear.updatedAt ? new Date(apiYear.updatedAt).getTime() : undefined,
});

export const toApiSettingsRefinements = ({ settings, projectRefinements }: SettingsPayloadInput): UpdateSettingsRefinementsBody => ({
  fieldId: projectRefinements.location.fieldSelect ?? null,
  marketPriceTrend: settings.market,
  costDevelopment: settings.costs,
  projectionYears: settings.timeline,
  machineryDiesel: projectRefinements.variableCosts.machineryDiesel,
  climateImpact: projectRefinements.modelSettings.climateImpact,
  rothCModel: projectRefinements.modelSettings.rothCModel,
  silverServiceVersion: projectRefinements.modelSettings.silverServiceVersion,
});

const toNumberOrDefault = (value: number | null | undefined, fallback: number): number => (value != null && Number.isFinite(value) ? value : fallback);

/** UI settings from the API row; missing or out-of-range values fall back to `defaults`. */
export const fromApiSettingsRefinements = (
  value: SoilSightSettingsRefinements | null | undefined,
  defaults: SettingsDefaults = DEFAULT_SETTINGS_DEFAULTS
): { settings: ScenarioSettings; projectRefinements: SettingsDefaults["project"] } => {
  const market = toNumberOrDefault(value?.marketPriceTrend, defaults.scenario.market);
  const costs = toNumberOrDefault(value?.costDevelopment, defaults.scenario.costs);
  const projection = toNumberOrDefault(value?.projectionYears, defaults.scenario.timeline);
  const timeline = toTimelineOption(projection, defaults.scenario.timeline);

  return {
    settings: { market, costs, timeline },
    projectRefinements: {
      location: {
        fieldSelect: value?.fieldId ?? defaults.project.location.fieldSelect,
      },
      variableCosts: {
        machineryDiesel: value?.machineryDiesel ?? null,
      },
      modelSettings: {
        climateImpact: value?.climateImpact ?? true,
        rothCModel: value?.rothCModel ?? true,
        silverServiceVersion: value?.silverServiceVersion ?? null,
      },
    },
  };
};

/** A UI scenario from a summary or detailed API payload; a summary without crop years gets empty starter years. */
export const fromApiScenario = (
  apiScenario: {
    id: string;
    name?: string | null;
    isReference: boolean;
    /** Anchors the calendar years in the Detailed calculations tab; falls back to the current year. */
    createdAt?: string;
  } & Partial<Pick<SoilSightScenarioDetail, "cropYears">>
): ScenarioData => {
  const apiYears = apiScenario.cropYears ?? [];
  const years =
    apiYears.length > 0
      ? apiYears.map((year) => fromApiCropYear(year, `${apiScenario.id}-y-${year.yearIndex}`))
      : Array.from({ length: SCENARIO_LIMITS.MIN_YEARS }, (_, index) => createEmptyYear(`${apiScenario.id}-y-${index + 1}`));

  return {
    id: apiScenario.id,
    createdAt: apiScenario.createdAt,
    title: (apiScenario.name ?? "").trim(),
    isReference: !!apiScenario.isReference,
    isBaseline: !!apiScenario.isReference,
    years,
  };
};
